import React, { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import "../../Styles/GenerateBill.css";
import html2pdf from "html2pdf.js";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../Components/Loader";

function GenerateBill() {
  const location = useLocation();
  const billRef = useRef(null);
  const [collegeDetails, setCollegeDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [isShared, setIsShared] = useState(false);

  // Updated default receipt data with more comprehensive default values
  const defaultReceiptData = {
    receiptNo: "RCP" + Math.floor(Math.random() * 100000).toString().padStart(5, '0'),
    receiptDate: new Date().toLocaleDateString("en-GB"),
    studentName: "Student Name",
    userCode: "N/A",
    refarence: "N/A",
    degreeClass: "N/A",
    referenceNumber: "REF" + Math.floor(Math.random() * 100000).toString().padStart(5, '0'),
    financialYear: `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
    billTitle: "Fee Receipt",
    fees: [{ sno: 1, particular: "N/A", amount: 0.0 }],
    totalAmount: 0.0,
    amountInWords: "INR Zero Rupees Only",
    paymentMode: "N/A",
    bank: "N/A BANK",
    remarks: "N/A",
  };

  // Use the updated defaultReceiptData in the initialization
  const receiptData = location.state?.receiptData || defaultReceiptData;

  useEffect(() => {
    fetchCollegeDetails();
  }, []);

  const fetchCollegeDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get("https://api.uniums.com/api/bill/college-details", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200 && response.data.details) {
        setCollegeDetails(response.data.details);
        // toast.success("College details fetched successfully.");
      } else {
        toast.info("No college details found.");
      }
    } catch (error) {
      console.error("Error fetching college details:", error);
      toast.error("Failed to fetch college details.");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format currency values properly
  const formatCurrency = (amount) => {
    return (Math.round(Number(amount) * 100) / 100).toFixed(2);
  };

  // Generate PDF and return the blob
  const generatePDFBlob = () => {
    return new Promise((resolve, reject) => {
      const element = billRef.current;
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `${receiptData.studentName.replace(/\s+/g, '_')}_fee_receipt.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          width: element.offsetWidth,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: true,
        },
        outputPdf: "blob"
      };

      html2pdf()
        .from(element)
        .set(opt)
        .outputPdf("blob")
        .then((blob) => {
          resolve(blob);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  // Function to handle share button click
  const handleShare = async () => {
    try {
      if (isShared) {
        if (!window.confirm("This receipt has already been shared. Are you sure you want to share it again?")) {
          return;
        }
      }
      setSharing(true);
      // Generate PDF blob
      const pdfBlob = await generatePDFBlob();

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("pdfFile", new File([pdfBlob], `${receiptData.studentName.replace(/\s+/g, '_')}_fee_receipt.pdf`, { type: "application/pdf" }));

      // Add receipt details to FormData
      formData.append("receiptNo", receiptData.receiptNo);
      formData.append("userCode", receiptData.userCode);
      formData.append("studentName", receiptData.studentName);
      formData.append("financialYear", receiptData.financialYr || receiptData.financialYear);
      formData.append("degree", receiptData.degree || receiptData.degreeClass);
      formData.append("totalAmount", receiptData.totalAmount);
      formData.append("paymentMode", receiptData.paymentMode);
      formData.append("remarks", receiptData.remarks);

      // Add the complete receipt details for reference
      formData.append("receiptDetails", JSON.stringify(receiptData));

      // Send to server
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        "https://api.uniums.com/api/bill/save-receipt",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
        }
      );

      if (response.status === 201) {
        toast.success("Receipt shared and saved successfully!");
      } else {
        toast.error("Failed to share receipt.");
      }
    } catch (error) {
      console.error("Error sharing receipt:", error);
      toast.error(error.response?.data?.message || "Failed to share receipt.");
    } finally {
      setSharing(false);
    }
  };

  // Function to download PDF locally (optional feature)
  const downloadPDF = async () => {
    const element = billRef.current;
    const opt = {
      margin: [10, 10, 10, 10],
      filename: `${receiptData.studentName.replace(/\s+/g, '_')}_fee_receipt.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        width: element.offsetWidth,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
      },
    };

    html2pdf().from(element).set(opt).save();
  };

  // Render loading state
  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <div className="generate-bill-container">
        <button
          className="share-btn"
          onClick={handleShare}
          disabled={sharing}
        >
          {sharing ? "Sharing..." : isShared ? "Share Receipt Again" : "Share Receipt"}
        </button>

        {isShared && (
          <div className="alert alert-info mt-2">
            This receipt has already been shared and saved.
          </div>
        )}
        <div className="new_class">
          <div className="receipt-preview" ref={billRef}>
            {/* Header (remains the same) */}
            <div className="receipt-header">
              <div className="institute-logo">
                <div className="logo-container">
                  <img
                    src={collegeDetails?.logo || "default-logo-url"}
                    alt="College Logo"
                  />
                </div>
              </div>
              <div className="institute-name">
                <h1>{collegeDetails?.fullName || 'College Name'}</h1>
                <p><strong>{collegeDetails?.subCollegeHeading || 'Sub College Heading'}</strong></p>
                <h4>
                  Official Website: {collegeDetails?.website || 'www.example.com'}
                </h4>
                <p>
                  Address: {collegeDetails?.address || 'N/A'} |
                  Pincode: {collegeDetails?.pincode || 'N/A'} |
                  Phone: {collegeDetails?.phoneNumber || 'N/A'}
                </p>
              </div>
            </div>

            {/* Receipt Title */}
            <div className="receipt-title">
              <h2>{receiptData.billTitle || 'Fee Receipt'}</h2>
              <h3>Student Copy</h3>
            </div>

            {/* Updated Student Details */}
            <div className="student-details">
              <table className="details-table">
                <tbody>
                  <tr>
                    <td width="20%">
                      <strong>Receipt No :</strong>
                    </td>
                    <td width="30%">{receiptData.receiptNo}</td>
                    <td width="20%">
                      <strong>Receipt Date :</strong>
                    </td>
                    <td width="30%">{receiptData.receiptDate}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Student Name :</strong>
                    </td>
                    <td>{receiptData.studentName}</td>
                    <td>
                      <strong>User Code :</strong>
                    </td>
                    <td>{receiptData.userCode}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Degree/Class :</strong>
                    </td>
                    <td>{receiptData.degree}</td>
                    <td>
                      <strong>Reference No :</strong>
                    </td>
                    <td>{receiptData.refarence}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Financial Year :</strong>
                    </td>
                    <td>{receiptData.financialYr}</td>
                    <td colSpan="2"></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Fee Details */}
            <div className="fee-details">
              <table className="fee-table">
                <thead>
                  <tr>
                    <th width="10%">S.NO</th>
                    <th width="70%">PARTICULARS</th>
                    <th width="20%">AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {receiptData.fees.map((fee) => (
                    <tr key={fee.sno}>
                      <td>{fee.sno}</td>
                      <td>{fee.particular}</td>
                      <td className="amount">{formatCurrency(fee.amount)}</td>
                    </tr>
                  ))}
                  {/* Empty rows to match the layout */}
                  {Array(10 - receiptData.fees.length)
                    .fill()
                    .map((_, index) => (
                      <tr key={`empty-${index}`} className="empty-row">
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                      </tr>
                    ))}
                </tbody>
                <tfoot>
                  <tr className="total-row">
                    <td colSpan="2">Total</td>
                    <td className="amount">
                      ₹ {formatCurrency(receiptData.totalAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Payment Details */}
            <div className="payment-details">
              <table className="payment-table">
                <tbody>
                  <tr>
                    <td width="20%">
                      <strong>In words :</strong>
                    </td>
                    <td width="80%">{receiptData.amountInWords}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Pay't Mode :</strong>
                    </td>
                    <td>{receiptData.paymentMode}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Bank :</strong>
                    </td>
                    <td>{receiptData.bank}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Remarks :</strong>
                    </td>
                    <td>{receiptData.remarks}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="receipt-footer">
              <p>This is Computer Generated Receipt</p>
              <div className="footer-notes">
                <p>
                  Note : Produce this receipt for future clarification in
                  respective of fees paid.
                </p>
                <p>*Collected on behalf of third party service provider</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
}

export default GenerateBill;